import { type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type LDContext,
  type LDFlagSet,
  type LDOptions,
  LDProvider,
  type LDReactOptions,
  useFlags,
  useLDClient,
} from 'launchdarkly-react-client-sdk';

import { appConfig } from '@ll-platform/frontend/config/app.config';
import { featureFlagsQueryKey } from '@ll-platform/frontend/config/featureFlags/consts';
import type {
  FeatureFlagName,
  FeatureFlags,
} from '@ll-platform/frontend/config/featureFlags/featureFlags';
import { FeatureFlagsProvider } from '@ll-platform/frontend/config/featureFlags/types';
import { activityTracker } from '@ll-platform/frontend/core/analytics/activityTracker';
import {
  buildEventName,
  isEventOnChannel,
} from '@ll-platform/frontend/core/analytics/helpers';
import {
  EventChannel,
  type TrackingEvent,
} from '@ll-platform/frontend/core/analytics/types';
import { useUser } from '@ll-platform/frontend/core/auth/hooks/useUser';
import { useFeatureFlagCustomContext } from '@ll-platform/frontend/core/featureFlags/FeatureFlagCustomContextProvider';
import { useOptionalActiveOrganization } from '@ll-platform/frontend/core/organization/ActiveOrganizationProvider';
import { useDeepCompareMemo } from '@ll-platform/frontend/utils/hooks/useDeepCompare';
import { assertDefined } from '@ll-platform/frontend/utils/types/types';

const options: LDOptions = {
  diagnosticOptOut: true,
  sendEventsOnlyForVariation: true,
  // We don't support updated to flags once the app is mounted except for active user updates
  streaming: false,
  application: {
    id: appConfig.REACT_APP_ENV,
    version: appConfig.REACT_APP_VERSION,
  },
};

const reactOptions: LDReactOptions = {
  sendEventsOnFlagRead: true, // required for analytics
  useCamelCaseFlagKeys: false,
};

const clientSideId = appConfig.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID;

function useLdContext() {
  const { activeUser, isLoading: isUserLoading } = useUser();
  const { activeOrganization } = useOptionalActiveOrganization();
  const { contexts } = useFeatureFlagCustomContext();

  const context = useDeepCompareMemo<LDContext | null>(() => {
    if (isUserLoading) {
      return null;
    }

    const customContexts = Array.from(contexts.entries());

    return {
      kind: 'multi',
      user: activeUser
        ? {
            kind: 'user',
            key: activeUser._id,
            email: activeUser.email,
            firstName: activeUser.firstName,
            lastName: activeUser.lastName,
            accountType: activeUser.accountType,
          }
        : {
            kind: 'user',
            key: 'anonymous',
            anonymous: true,
          },
      ...(activeOrganization && {
        organization: {
          kind: 'organization',
          key: activeOrganization.id,
        },
      }),
      ...Object.fromEntries(
        customContexts.map(([name, value]) => [name, { kind: name, ...value }]),
      ),
    };
  }, [activeUser, activeOrganization, isUserLoading, contexts]);

  return context;
}

const LaunchDarklyInnerContextUpdater = () => {
  const ldClient = useLDClient();
  const ldIdentifyContext = useLdContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!ldIdentifyContext || !ldClient) {
      return;
    }

    const handleDone = (_error: unknown, flags: LDFlagSet | null) => {
      if (!flags) {
        return;
      }
      if (appConfig.REACT_APP_FEATURE_FLAGS_DEBUG) {
        console.debug(
          '[FeatureFlags] LaunchDarkly Identify Completed',
          ldIdentifyContext,
          flags,
        );
      }
      // LaunchDarkly useFlags doesn't always re-render
      queryClient.setQueryData(
        [featureFlagsQueryKey, FeatureFlagsProvider.LaunchDarkly],
        flags as Partial<FeatureFlags>,
      );
    };

    // WARN: We don't trigger loading state again after initial load
    // No flags currently need that
    ldClient.identify(ldIdentifyContext, undefined, handleDone);

    return activityTracker.subscribe('LaunchDarkly', (event: TrackingEvent) => {
      if (!isEventOnChannel(event, EventChannel.Experiments, true)) {
        // excluded All channel for now
        return;
      }
      ldClient?.track(
        buildEventName(event.type, '-'),
        event.metadata,
        typeof event.metadata?.value === 'number'
          ? event.metadata.value
          : undefined,
      );
    });
  }, [ldClient, ldIdentifyContext, queryClient]);

  return null;
};

export const LaunchDarklyProvider = ({ children }: PropsWithChildren) => {
  const ldContext = useLdContext();

  if (!clientSideId) {
    return children;
  }

  return (
    <LDProvider
      clientSideID={clientSideId}
      timeout={2}
      context={ldContext ?? undefined}
      reactOptions={reactOptions}
      options={options}
      deferInitialization
    >
      <LaunchDarklyInnerContextUpdater />
      {children}
    </LDProvider>
  );
};

export function useLaunchDarklyFeatureFlags() {
  const queryClient = useQueryClient();
  const ldClient = useLDClient();

  // We can't use the standard async LD loader because
  // 1. We need the app to be rendered while waiting for flags
  // 2. We integrate multiple providers
  const waitForInitialization = useCallback(async () => {
    assertDefined(ldClient, 'ldClient');
    await ldClient.waitForInitialization(2);
    const flags = ldClient.allFlags() as Partial<FeatureFlags>;

    return flags;
  }, [ldClient]);

  const query = useQuery({
    // active user is not needed in query key as it is automatically updated with useEffect
    queryKey: [featureFlagsQueryKey, FeatureFlagsProvider.LaunchDarkly],
    queryFn: waitForInitialization,
    retry: 1,
    gcTime: Infinity,
    staleTime: Infinity,
    meta: {
      supressErrorToast: true,
    },
    enabled: !!ldClient,
  });

  const flags = useFlags() as Partial<FeatureFlags>;

  const trackExposure = useCallback(
    (flag: FeatureFlagName) => {
      ldClient?.variation(flag);
    },
    [ldClient],
  );

  useEffect(() => {
    if (!query.isSuccess) {
      return;
    }

    queryClient.setQueryData(
      [featureFlagsQueryKey, FeatureFlagsProvider.LaunchDarkly],
      flags,
    );
  }, [flags, query.isSuccess, queryClient.setQueryData]);

  useEffect(() => {
    if (!clientSideId) {
      queryClient.setQueryData(
        [featureFlagsQueryKey, FeatureFlagsProvider.LaunchDarkly],
        {},
      );
    }
  }, [queryClient.setQueryData]);

  return useMemo(() => ({ query, trackExposure }), [query, trackExposure]);
}
